import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/common';
import './UserPool.css';

export default function UserPool() {
  const navigate = useNavigate();

  // ------------ STATE ------------
  const [devices, setDevices] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0);
const [selectedUnits, setSelectedUnits] = useState(1);     // kWh
const [selectedAmount, setSelectedAmount] = useState(0);  // ₹


  const [isLoading, setIsLoading] = useState(true);
  const [isTopupLoading, setIsTopupLoading] = useState(false);
  const [error, setError] = useState(null);

  // ------------ DERIVED ------------
  const currentDevice = devices[currentDeviceIndex];
  const totalDevices = devices.length;

  const ratePerKwh = currentDevice?.rate || 10.0;

  const maxUnits = walletBalance > 0 
    ? Math.floor((walletBalance / ratePerKwh) * 1000) / 1000 
    : 0;

// Clamp helpers
const clampUnits = (val) => Math.min(Math.max(val, 0), maxUnits);
const clampAmount = (val) => Math.min(Math.max(val, 0), walletBalance);

// Sync values
const syncFromUnits = (units) => {
  const safeUnits = clampUnits(units);
  const amount = Math.round(safeUnits * ratePerKwh * 100) / 100;

  setSelectedUnits(safeUnits);
  setSelectedAmount(clampAmount(amount));
};

const syncFromAmount = (amount) => {
  const safeAmount = clampAmount(amount);
  const units = Math.floor((safeAmount / ratePerKwh) * 1000) / 1000;

  setSelectedAmount(safeAmount);
  setSelectedUnits(clampUnits(units));
};


  const isDeviceActive = currentDevice?.isActive === true;

  // ------------ NEW: Device ID Lookup Cache ------------
  const [deviceMongoIds, setDeviceMongoIds] = useState({});

  // ------------ API CALLS ------------

  const fetchUserHomeData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      const response = await fetch('https://viz-sm-be.onrender.com/api/v1/user/home', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`API failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ User Data:', data);

      const balance = data.balance ?? 0;
      const linkedDevices = data.devices ?? [];

      // ✅ ENHANCE: Store both display ID and MongoDB lookup
      const enhancedDevices = linkedDevices.map(device => ({
        ...device,
        displayId: device.id,  // SM100001 (for display)
        mongoId: null          // Will be filled by lookup
      }));

      console.log('💰 Wallet:', balance);
      console.log('📱 Devices:', enhancedDevices);

      setWalletBalance(balance);
      setDevices(enhancedDevices);

      if (!enhancedDevices.length) {
        setError('No linked devices found. Please link a device first.');
      } else {
        setCurrentDeviceIndex(0);
        setSelectedUnits(1);
      }
    } catch (err) {
      console.error('❌ Error:', err);
      setError(err.message);
      setDevices([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ NEW: Lookup REAL MongoDB ObjectId for device
  const getDeviceMongoId = async (displayId) => {
    // Check cache first
    if (deviceMongoIds[displayId]) {
      return deviceMongoIds[displayId];
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://viz-sm-be.onrender.com/api/v1/devices/find', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ deviceId: displayId }),
      });

      if (response.ok) {
        const device = await response.json();
        const mongoId = device._id;
        
        // Cache result
        setDeviceMongoIds(prev => ({ ...prev, [displayId]: mongoId }));
        return mongoId;
      }
    } catch (err) {
      console.warn('Device lookup failed:', err);
    }

    // ✅ FALLBACK: Try direct lookup in devices array
    const device = devices.find(d => d.displayId === displayId);
    if (device?.mongoId) return device.mongoId;

    // 🚨 ULTIMATE FALLBACK: Use displayId (might fail)
    return displayId;
  };

const handleTopup = async () => {
  if (!currentDevice) {
    alert('Please select a device first');
    return;
  }

  if (selectedAmount <= 0) {
    alert('Please select some units');
    return;
  }

  if (selectedAmount > walletBalance) {
    alert('Insufficient wallet balance');
    return;
  }

const confirmMsg = `Topup ${selectedUnits.toFixed(3)} kWh (₹${selectedAmount.toFixed(2)}) to ${currentDevice.id}?`;
  if (!window.confirm(confirmMsg)) return;

  try {
    setIsTopupLoading(true);
    setError(null);

    const token = localStorage.getItem('authToken');
    
    const payload = {
      // ✅ FIX 1: UPPERCASE (TopupDevice validates .toUpperCase())
      deviceId: currentDevice.id.toUpperCase(),  
      // ✅ FIX 2: INTEGER amount (Math.floor avoids float precision)
      amount: Math.floor(selectedAmount * 100) / 100,  
      // ✅ FIX 3: INTEGER rate (TopupDevice expects exact match)
      rate: Math.floor(ratePerKwh * 100) / 100,       
      currency: 'INR',
    };

    console.log('🚀 FIXED TOPUP PAYLOAD:', payload);

    const response = await fetch('https://viz-sm-be.onrender.com/api/v1/user/topupdevice', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    console.log('📡 RESPONSE:', response.status);

    if (!response.ok) {
      const responseText = await response.text();
      console.log('❌ FULL ERROR:', responseText);
      
      let errorMsg = `Topup failed (${response.status})`;
      try {
        const errData = JSON.parse(responseText);
        if (errData?.alerts?.[0]?.message) {
          errorMsg = errData.alerts[0].message;
        }
      } catch {}
      
      throw new Error(errorMsg);
    }

    // ✅ SUCCESS! Refresh data
    alert(`✅ SUCCESS! ${selectedUnits.toFixed(3)} kWh added to ${currentDevice.id}`);
    await fetchUserHomeData();
    setSelectedUnits(1);
    
  } catch (err) {
    console.error('❌ Topup error:', err);
    alert(err.message);
  } finally {
    setIsTopupLoading(false);
  }
};



  // ------------ FIXED NAVIGATION ------------
  const handlePrevDevice = useCallback(() => {
    if (totalDevices <= 1) return;
    setCurrentDeviceIndex((prev) => 
      prev === 0 ? totalDevices - 1 : prev - 1
    );
  }, [totalDevices]);

  const handleNextDevice = useCallback(() => {
    if (totalDevices <= 1) return;
    setCurrentDeviceIndex((prev) => 
      prev === totalDevices - 1 ? 0 : prev + 1
    );
  }, [totalDevices]);

const handleSliderChange = (e) => {
  const value = Number(e.target.value);
  if (!Number.isNaN(value)) {
    syncFromUnits(value);
  }
};


  const handleBack = () => navigate('/home');

  // ------------ LIFECYCLE ------------
  useEffect(() => {
    fetchUserHomeData();
  }, []);

  // ------------ RENDER ------------

  if (isLoading) {
    return (
      <div className="pool-topup-root">
        <header className="pool-topup-header">
          <div className="pool-header-side">
            <button className="pool-back-btn" onClick={handleBack}>‹</button>
          </div>
          <div className="pool-header-center">
            <h1 className="pool-topup-title">Pool Topup</h1>
          </div>
          <div className="pool-header-side" />
        </header>
        <main className="pool-topup-container">
          <p className="pool-instruction">Loading your devices...</p>
        </main>
      </div>
    );
  }

  return (
    <div className="pool-topup-root">
      {/* HEADER */}
      <header className="pool-topup-header">
        <div className="pool-header-side">
          <button className="pool-back-btn" onClick={handleBack}> 
            <i className="fas fa-arrow-left"></i> </button>
        </div>
        <div className="pool-header-center">
          <h1 className="pool-topup-title">POOL Topup</h1>
        </div>
        <div className="pool-header-side" />
      </header>

      <main className="pool-topup-container">
        {/* INSTRUCTION */}
        <p className="pool-instruction">
          *Transfer from WALLET to POOL is non-refundable
        </p>

        {/* WALLET DISPLAY */}
        <div style={{ 
          width: '70%', 
          textAlign: 'center', 
          background: 'transparent', 
          padding: '0.7rem', 
          borderRadius: '1rem',
          marginBottom: '0rem'
        }}>
          <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#0A1F35' }}>
            WALLET : ₹ {walletBalance.toFixed(1)}
          </div>
        </div>

        {/* ✅ FIXED DEVICE CAROUSEL */}
        <section className="pool-carousel-section">
          {totalDevices > 1 && (
            <>
              <button 
                type="button" 
                className="pool-carousel-btn prev" 
                onClick={handlePrevDevice}
                disabled={isTopupLoading}
              >
                ‹
              </button>
              <button 
                type="button" 
                className="pool-carousel-btn next" 
                onClick={handleNextDevice}
                disabled={isTopupLoading}
              >
                ›
              </button>
            </>
          )}

          {currentDevice ? (
            <div className="pool-device-card">
              {/* ✅ FIXED: POOL Balance (not wallet) */}
              <div className="pool-card-header">
                <span className="pool-card-link"></span>
                <div className="pool-card-balance">
                  {currentDevice.pool?.toFixed(1)} kWh
                </div>
              </div>

              {/* ✅ FIXED: Device ID (uses "id" from your data) */}
              <div className="pool-card-label">Serial Number</div>
              <div className="pool-card-serial">
                {currentDevice.id || 'UNKNOWN'}
              </div>
              <div className="pool-card-title">POOL</div>

              {/* ✅ Status dot */}
              <div className={`pool-device-status ${isDeviceActive ? 'online' : 'offline'}`} />
            </div>
          ) : (
            <div className="pool-device-card" style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="pool-card-label">No Device</div>
              <div className="pool-card-serial">Select a device</div>
            </div>
          )}
        </section>

        {/* ✅ DEVICE COUNTER */}
        {totalDevices > 0 && (
          <div style={{ 
            width: '90%', 
            textAlign: 'center', 
            fontSize: '0.85rem', 
            color: '#666',
            fontWeight: '500'
          }}>
            Device {currentDeviceIndex + 1} of {totalDevices}
          </div>
        )}

        {/* DEVICE INFO */}
        {currentDevice && (
          <section className="pool-device-info">
            <div className="pool-info-row">
              <div className="pool-info-label">
                Current POOL: <strong>{currentDevice.pool?.toFixed(1)} kWh</strong>
              </div>
              <div className="pool-info-label">
                Rate: <strong>₹{ratePerKwh.toFixed(1)}/kWh</strong>
              </div>
            </div>
          </section>
        )}

        {/* SLIDER */}
        <section className="pool-slider-section">
          <div className="pool-slider-labels">
            <span className="pool-slider-min">0 kWh</span>
            <span className="pool-slider-mid">Select Units</span>
            <span className="pool-slider-max">
              Max: {maxUnits.toFixed(1)} kWh
            </span>
          </div>

          <input
            type="range"
            className="pool-slider"
            min="0"
            max={Math.max(maxUnits, 1)}
            step="0.01"
            value={selectedUnits}
            onChange={handleSliderChange}
            disabled={!currentDevice || maxUnits <= 0 || isTopupLoading}
          />



        </section>

        {/* SUMMARY */}
<section className="pool-summary">
  <p className="pool-summary-line inline-edit">
    Transfer&nbsp;

    <input
      type="number"
      step="0.01"
      min="0"
      max={maxUnits}
      value={selectedUnits}
      onChange={(e) => syncFromUnits(Number(e.target.value))}
      className="inline-input"
    />
    &nbsp;kWh&nbsp; 

    (₹
    <input
      type="number"
      step="0.01"
      min="0"
      max={walletBalance}
      value={selectedAmount}
      onChange={(e) => syncFromAmount(Number(e.target.value))}
      className="inline-input"
    />
    )
    &nbsp;from WALLET to POOL
  </p>
</section>


        {/* BUTTON */}
        <div className="pool-topup-btn">
          <Button
            className="pool-confirm-btn"
            onClick={handleTopup}
            disabled={
              !currentDevice || 
              isTopupLoading || 
              selectedAmount <= 0 || 
              selectedAmount > walletBalance
            }
          >
            {isTopupLoading ? 'Processing...' : 'Confirm Topup'}
          </Button>
        </div>

      </main>
    </div>
  );
}
